import React, { useEffect, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disableLink?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Введите текст...',
  className = '',
  disableLink = false,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lockRef = useRef<{ el: HTMLElement | null; top: number; until: number }>({ el: null, top: 0, until: 0 });
  const boundScrollElRef = useRef<HTMLElement | null>(null);
  const boundScrollHandlerRef = useRef<((e: Event) => void) | null>(null);

  const resolveScrollContainer = (root: HTMLElement): HTMLElement | null => {
    return (
      (root.closest('.custom-scrollbar') as HTMLElement | null) ??
      (document.querySelector('.custom-scrollbar') as HTMLElement | null) ??
      (document.scrollingElement as HTMLElement | null)
    );
  };

  const detachScrollHandler = () => {
    if (boundScrollElRef.current && boundScrollHandlerRef.current) {
      boundScrollElRef.current.removeEventListener('scroll', boundScrollHandlerRef.current);
    }
    boundScrollElRef.current = null;
    boundScrollHandlerRef.current = null;
  };

  const attachScrollHandler = (el: HTMLElement) => {
    if (boundScrollElRef.current === el) return;
    detachScrollHandler();
    const handler = () => {
      const { el: lockedEl, top, until } = lockRef.current;
      if (!lockedEl) return;
      if (performance.now() < until) {
        lockedEl.scrollTop = top;
      }
    };
    el.addEventListener('scroll', handler, { passive: true } as any);
    boundScrollElRef.current = el;
    boundScrollHandlerRef.current = handler;
  };

  const startScrollLock = (root: HTMLElement) => {
    const el = resolveScrollContainer(root);
    if (!el) return;
    attachScrollHandler(el);
    lockRef.current = { el, top: el.scrollTop, until: performance.now() + 50 };
    const tick = () => {
      const { el: lockedEl, top, until } = lockRef.current;
      if (!lockedEl) return;
      lockedEl.scrollTop = top;
      if (performance.now() < until) {
        rafIdRef.current = requestAnimationFrame(tick);
      } else {
        rafIdRef.current = null;
      }
    };
    if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(tick);
  };

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ...(disableLink ? [] : [['link']]),
        ['clean'],
      ],
    }),
    [disableLink]
  );

  const formats = useMemo(() => [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    ...(disableLink ? [] : ['link']),
  ], [disableLink]);

  useEffect(() => {
    const quill = quillRef.current;
    const root = quill?.getEditor?.()?.root as HTMLElement | undefined;
    if (!root) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const isPaste = (e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V');
      if (isPaste) startScrollLock(root);
    };
    const onPaste = () => startScrollLock(root);

    root.addEventListener('keydown', onKeyDown, true);
    root.addEventListener('paste', onPaste, true);

    return () => {
      root.removeEventListener('keydown', onKeyDown, true);
      root.removeEventListener('paste', onPaste, true);
      detachScrollHandler();
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    };
  }, []);

  return (
    <div className={`rich-text-editor ${className}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          minHeight: '200px',
        }}
      />
      <style>{`
        .rich-text-editor .quill {
          background: #ffffff;
          overflow-anchor: none;
        }
        .rich-text-editor .ql-toolbar {
          background: #f8f9fa;
          border: 1px solid var(--border-color, #e0e0e0);
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
        .rich-text-editor .ql-container {
          border: 1px solid var(--border-color, #e0e0e0);
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
          font-size: 14px;
          min-height: 200px;
          overflow-anchor: none;
        }
        .rich-text-editor .ql-editor {
          min-height: 200px;
          overflow-anchor: none;
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #999;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};
